import { AutoComplete, AutoCompleteProps, Spin } from 'antd';
import debounce from 'lodash/debounce';
import { useMemo, useRef, useState } from 'react';

import { NurseProfileDto } from '@clh/api-client';
import { unwrapError, useApiClient } from '@clh/ui';

import { useToast } from '../hooks/use-toast';

const ProfileAutoComplete = ({
    onSelect,
    autoFocus = true,
    disabled,
    placeholder,
    allowClear,
    onClear,
    role,
}: {
    onSelect: (profile: NurseProfileDto) => void;
    autoFocus?: boolean;
    disabled?: boolean;
    placeholder?: string;
    allowClear?: boolean;
    onClear?: () => void;
    role?: string;
}) => {
    const [options, setOptions] = useState<AutoCompleteProps['options']>([]);
    const api = useApiClient();
    const { notification } = useToast();

    const [fetching, setFetching] = useState(false);
    const fetchRef = useRef(0);

    const debouncedSearch = useMemo(() => {
        const loadOptions = (query: string) => {
            fetchRef.current += 1;
            const fetchId = fetchRef.current;
            setOptions([]);
            setFetching(true);
            api?.profileControllerListProfiles({
                search: query,
                role,
            })
                .then((profiles) => {
                    if (fetchId !== fetchRef.current) {
                        return;
                    }
                    setOptions(
                        profiles.map((profile) => ({
                            value: `${profile.firstName} ${profile.lastName}`,
                            label: `${profile.firstName} ${profile.lastName}`,
                            id: profile.id,
                            firstName: profile.firstName,
                            lastName: profile.lastName,
                        }))
                    );

                    setFetching(false);
                })
                .catch((err) => {
                    notification.error({
                        message: unwrapError(err),
                        duration: 3,
                    });
                });
        };

        return debounce(loadOptions, 800);
    }, [api, role]);

    return (
        <AutoComplete
            autoFocus={autoFocus}
            options={options}
            placeholder={placeholder}
            allowClear={allowClear}
            disabled={disabled}
            onClear={onClear}
            onSearch={(query: string) => debouncedSearch(query)}
            onSelect={(_value, option) =>
                onSelect({
                    id: option.id,
                    firstName: option.firstName,
                    lastName: option.lastName,
                })
            }
            style={{ width: 200 }}
            notFoundContent={fetching ? <Spin size="small" /> : null}
        ></AutoComplete>
    );
};

export default ProfileAutoComplete;
